// Direct R2 upload with proper AWS Signature V4

const R2_CONFIG = {
  accountId: '4fa157e5f94c7cd59dfbc3fdf3df9df8',
  accessKeyId: '6a06d0b91d4d83e6b4e450c49f0d0e48',
  secretAccessKey: '238f65a8b2e07fac19420308bd894b41af41e61bf8113019f53f4575c448cfac',
  bucketName: 'ypl-11-storage',
  publicBucketUrl: 'https://pub-1f770a29f9504238bfcefd1f1f5963dd.r2.dev',
}

async function sha256(message: string | ArrayBuffer): Promise<string> {
  let data: BufferSource
  if (typeof message === 'string') {
    const encoder = new TextEncoder()
    data = encoder.encode(message) as BufferSource
  } else {
    data = new Uint8Array(message) as BufferSource
  }
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

async function hmacString(key: string, message: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder()
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  return crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(message))
}

async function hmacBuffer(key: ArrayBuffer, message: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder()
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  return crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(message))
}

async function getSignatureKey(secretKey: string, dateStamp: string, regionName: string, serviceName: string): Promise<ArrayBuffer> {
  const kDate = await hmacString('AWS4' + secretKey, dateStamp)
  const kRegion = await hmacBuffer(kDate, regionName)
  const kService = await hmacBuffer(kRegion, serviceName)
  const kSigning = await hmacBuffer(kService, 'aws4_request')
  return kSigning
}

async function uploadToR2(file: File, objectPath: string): Promise<string> {
  const endpoint = `https://${R2_CONFIG.accountId}.r2.cloudflarestorage.com`
  const url = `${endpoint}/${R2_CONFIG.bucketName}/${objectPath}`

  // Read file
  const fileBuffer = await file.arrayBuffer()
  const contentType = file.type || 'image/jpeg'

  // Calculate SHA256 hash of payload
  const payloadHash = await sha256(fileBuffer)

  // Get dates
  const now = new Date()
  const dateStamp = now.toISOString().slice(0, 10).replace(/-/g, '')
  const amzDate = dateStamp + 'T' + now.toISOString().slice(11, 19).replace(/:/g, '') + 'Z'

  // Create canonical request
  const canonicalHeaders = `host:${R2_CONFIG.accountId}.r2.cloudflarestorage.com\n` +
    `x-amz-content-sha256:${payloadHash}\n` +
    `x-amz-date:${amzDate}\n`

  const signedHeaders = 'host;x-amz-content-sha256;x-amz-date'

  const canonicalRequest = `PUT\n/${R2_CONFIG.bucketName}/${objectPath}\n\n${canonicalHeaders}\n${signedHeaders}\n${payloadHash}`

  // Create string to sign
  const credentialScope = `${dateStamp}/auto/s3/aws4_request`
  const stringToSign = `AWS4-HMAC-SHA256\n${amzDate}\n${credentialScope}\n${await sha256(canonicalRequest)}`

  // Calculate signature
  const signingKey = await getSignatureKey(R2_CONFIG.secretAccessKey, dateStamp, 'auto', 's3')
  const signatureBuffer = await hmacBuffer(signingKey, stringToSign)
  const signature = Array.from(new Uint8Array(signatureBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')

  // Create authorization header
  const authorization = `AWS4-HMAC-SHA256 Credential=${R2_CONFIG.accessKeyId}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`

  // Make request
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      'x-amz-date': amzDate,
      'x-amz-content-sha256': payloadHash,
      'Authorization': authorization,
    },
    body: fileBuffer,
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`)
  }

  return `${R2_CONFIG.publicBucketUrl}/${objectPath}`
}

export const storageRepository = {
  async uploadProfilePhoto(file: File, playerName?: string): Promise<string> {
    const sanitizedName = playerName?.trim().replaceAll(' ', '_') || 'temp'
    const timestamp = Date.now()
    const fileName = `Profile_${sanitizedName}.jpg`
    const objectPath = `profile-photos/${timestamp}_${fileName}`

    return uploadToR2(file, objectPath)
  },

  async uploadPaymentScreenshot(file: File, playerName?: string): Promise<string> {
    const sanitizedName = playerName?.trim().replaceAll(' ', '_') || 'temp'
    const timestamp = Date.now()
    const fileName = `Payment_${sanitizedName}.jpg`
    const objectPath = `payment-screenshots/${timestamp}_${fileName}`

    return uploadToR2(file, objectPath)
  },
}
